import { MenuItem, Select } from "@material-ui/core";
import { runInAction, toJS } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useState } from "react";
import { Asterisk } from "../../components/Asterisk";
import { BootstrapInput } from "../../components/BootstrapInput";
import EventLabel from "../../components/EventLabel";
import { toCamelCase } from "../../helpers/objectHelper";
import { Event } from "../Event/Event";

export const EventGroup = observer((props) => {
  const { settings, breakouts, displayEventSettings, contentModuleId, contentLayoutModuleId, validations, activePersonNo, environment, updateOrderRow, checkConditions, checkEventAvailability, validate } = props;
  const validity = validations.find((v) => v.id == contentModuleId);
  const orders = toJS(props.orders);
  const order = orders[activePersonNo];
  const { overrideDisplay, required, min, max, showTitle, titleText = {}, showSubtitle, subtitleText = {} } = settings;

  const eventList = useMemo(
    () =>
      Array.from(settings.eventIds)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((e) => breakouts.find((m) => e.eventId == m.eventId)),
    [settings.eventIds, breakouts]
  );

  const checkedRows = useMemo(() => order?.orderRow?.filter((or) => settings.eventIds.map((e) => e.eventId).includes(or.eventId)) || [], [order?.orderRow]);

  const activeDisplaySettings = useMemo(() => {
    if (overrideDisplay) {
      const o = {};
      for (const key in displayEventSettings) {
        if (Object.hasOwnProperty.call(displayEventSettings, key)) {
          o[key] = settings[toCamelCase(key)] === true;
        }
      }
      return o;
    } else {
      return displayEventSettings;
    }
  }, [
    overrideDisplay,
    settings?.showEventStartDateTime,
    settings?.showEventStartTime,
    settings?.showEventEndDateTime,
    settings?.showEventEndTime,
    settings?.showEventCity,
    settings?.showEventVenue,
    settings?.showEventDescription,
    settings?.showEventPrice,
    settings?.showEventName,
    settings?.showEventIncVat,
    settings?.showEventVatPrecentage,
    settings?.showEventVatSum,
  ]);

  useEffect(() => {
    validateGroup();
  }, [checkedRows]);

  const validateGroup = () => {
    const dues = eventList.filter((b) => (b.deadline ? +new Date(b.deadline) < +new Date() : false))?.length;

    let isValid = true;
    let reason = "";

    if (dues - eventList?.length < min) {
      isValid = true;
      reason = "due";
    } else if (required && !checkedRows.length) {
      isValid = false;
      reason = "required";
    } else if (min > 0 && checkedRows.length < min && required) {
      isValid = false;
      reason = "min";
    } else if (max > 0 && checkedRows.length > max && required) {
      isValid = false;
      reason = "max";
    }

    if (validity) {
      runInAction(() => {
        validity.valid = isValid;
        validity.reason = reason;
      });
    }
  };

  const uncheckGroupActivities = (id) => {
    if (checkedRows.length > 0 && !checkedRows.find((c) => c.eventId == id)) {
      checkedRows.map((row) => {
        updateOrderRow(row, true);
      });
    }
  };

  const isValid = useMemo(() => {
    if (validate && validations.find((v) => v.id === contentModuleId)?.valid === false) return false;
    return undefined;
  }, [validate, validations, contentModuleId]);

  if (settings.eventIds.length == 0) return null;
  if (settings.hidden) return null;

  return (
    <>
      {settings.displayType === "select" ? (
        <EventGroupSelector
          options={eventList}
          showPlaceholder={true}
          updateOrderRow={updateOrderRow}
          uncheckGroupActivities={uncheckGroupActivities}
          title={titleText[environment.lang]}
          subtitle={subtitleText[environment.lang]}
          required={settings.required}
          breakouts={breakouts}
          checkConditions={() => checkConditions(contentLayoutModuleId)}
          parentId={contentModuleId}
          checkedRows={checkedRows}
          checkEventAvailability={(eventId) => checkEventAvailability(eventId, orders.length > 0 && orders[activePersonNo].orderId ? orders[activePersonNo].orderId : null, props.sqlStateNo, true)}
        />
      ) : (
        <>
          {showTitle ? <h1 className="cms-eventgroup-title">{titleText[environment.lang] || ""}</h1> : null}
          {showSubtitle ? <h2 className="cms-eventgroup-subtitle">{subtitleText[environment.lang] || ""}</h2> : null}
          {eventList.map((event, index) => {
            if (!event || !event.eventId) return null;
            const localSettings = {
              eventId: event.eventId,
              prechecked: false,
              prefixClassName: "cms-event",
              show: {},
              validation: {},
              contentLayoutModuleId,
              readonly:
                settings.readonly ||
                (max > 1 &&
                  checkedRows.length >= max &&
                  !checkedRows
                    .map((e) => {
                      return e.eventId;
                    })
                    .includes(event.eventId)),
              displayType: settings.displayType,
              onlySelectOne: max === 1,
              radio: (max <= 1 && required) || settings.radio,
            };
            return (
              <div className="cms-event" key={index}>
                <Event
                  hasParent={true}
                  validateGroup={validateGroup}
                  parentValid={isValid}
                  parentId={contentModuleId}
                  {...{ ...props, settings: localSettings, displayEventSettings: activeDisplaySettings }}
                  uncheckGroupActivities={uncheckGroupActivities}
                />
              </div>
            );
          })}
        </>
      )}
    </>
  );
});

const EventGroupSelector = ({ options, showPlaceholder, placeholderText, title, subtitle, required, uncheckGroupActivities, updateOrderRow, checkConditions, order, parentId, checkedRows, checkEventAvailability }) => {
  const [disabledEvents, setDisabledEvents] = useState([]);

  useEffect(() => {
    (async () => {
      const list = [];
      for (const ev of options) {
        if (!(await checkEventAvailability(ev))) {
          list.push(ev.eventId);
        }
      }
      setDisabledEvents(list);
    })();
  }, [options, order]);

  const selectedEvents = useMemo(() => checkedRows.map((l) => l.eventId), [checkedRows]);

  const handleChange = (value) => {
    uncheckGroupActivities(value);
    let breakout = options.find((b) => b.eventId == value);
    if (breakout) {
      updateOrderRow({
        amount: breakout.amount ?? 0,
        comment: "",
        discount: 0,
        eventId: breakout.eventId,
        orderId: -1,
        orderRowId: ((order?.orderRow?.length ?? 0) + 1) * -1,
        quantity: 1,
        status: null,
        vatPercent: breakout.vatPercent || 0,
        deleted: false,
        groupId: parentId,
      });
    }
    checkConditions();
  };

  return (
    <div className="cms-field form-group">
      <div className="title-holder">
        {title ? (
          <label className="flex">
            {title}
            {required ? <Asterisk /> : null}
          </label>
        ) : null}
        {subtitle ? <label className="sublabel">{subtitle}</label> : null}
      </div>
      <div className="select-wrap">
        <Select
          className="selector"
          inputProps={{
            className: "selector-menu",
            style: {
              width: "calc(100%)",
            },
          }}
          MenuProps={{
            MenuListProps: {
              className: "menu-list",
            },
          }}
          displayEmpty={true}
          defaultValue=""
          value={selectedEvents}
          onChange={(e) => handleChange(e.target.value)}
          input={<BootstrapInput />}
        >
          <MenuItem value="">
            <em>{placeholderText ? placeholderText : <EventLabel name="EventGroupListPlaceholder" groupName="CMS.FormEvent" />}</em>
          </MenuItem>
          {options.map((event, index) => {
            if (!event || !event.eventId) return null;
            const disabled = disabledEvents.includes(event.eventId);
            return (
              <MenuItem key={event.eventId} value={event.eventId} disabled={disabled}>
                {event.name}{" "}
                {disabled ? (
                  <span>
                    &nbsp; <EventLabel name="FullBooked" groupName="CMS.FormEvent" />
                  </span>
                ) : null}
              </MenuItem>
            );
          })}
        </Select>
      </div>
    </div>
  );
};
